import React, { useMemo } from 'react';
import { TextFilter, SelectFilter, MultiSelectFilter } from './common/FilterControls';
import { useGetEventsFilterOptionsDeviceTypes } from '../api/generated/arrayscapeGamingDashboardAPI';
import { useAllUsers } from '../hooks/useAllUsers';

export interface EventFiltersState {
  dateRange: { start_date: string; end_date: string };
  sales_person?: string;
  sales_lead?: string;
  owner?: string;
  customer?: string;
  location?: string;
  state?: string;
  activity?: string;
  activity_type?: string;
  device_types?: string;
  reviewed?: string;
  tech_setup_required?: string;
  tech_setup_completed?: string;
  invoiced?: string;
  invoice_number?: string;
  customer_type?: string;
  need_review?: string;
  need_tech_setup?: string;
  need_pre_program_email?: string;
  need_reminder_email?: string;
  need_sling_shift?: string;
  sling_location?: string;
  sling_error?: string;
}

interface EventFilterBarProps {
  filters: EventFiltersState;
  onFiltersChange: (filters: EventFiltersState) => void;
  onClearFilters: () => void;
}

const EventFilterBar: React.FC<EventFilterBarProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
}) => {
  const [isClearing, setIsClearing] = React.useState(false);

  // Device-type options come from the backend (tokenized +
  // normalized + frequency-sorted from events.program_devices).
  const { data: deviceTypesResp } = useGetEventsFilterOptionsDeviceTypes();
  const deviceTypeOptions = useMemo(
    () => (deviceTypesResp?.data ?? [])
      .filter(o => o.value && o.label)
      .map(o => ({ value: o.value!, label: o.label! })),
    [deviceTypesResp],
  );

  // Owner filter options: active users plus an explicit "Unassigned" choice
  // (maps to the backend's `owner=unassigned` sentinel → owner_id IS NULL).
  const { data: allUsers } = useAllUsers();
  const ownerOptions = useMemo(
    () => [
      { value: 'unassigned', label: 'Unassigned' },
      ...(allUsers ?? [])
        .filter(u => u.active && u.id)
        .map(u => ({
          value: u.id!,
          label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.id!,
        })),
    ],
    [allUsers],
  );

  const handleFilterChange = (field: string, value: string | undefined) => {
    // Don't coerce '' → undefined. MultiSelectFilter uses '' to signal
    // "Deselect all" (explicit empty, distinct from undefined which
    // means "no filter, all selected"). Page state must preserve that
    // distinction or the Deselect-all button silently no-ops.
    onFiltersChange({
      ...filters,
      [field]: value,
    });
  };

  // Count active filters (excluding dateRange and "need" filters which are handled by cards)
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    return value &&
           key !== 'dateRange' &&
           key !== 'need_review' &&
           key !== 'need_tech_setup' &&
           key !== 'need_pre_program_email' &&
           key !== 'need_reminder_email';
  }).length;

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div className="ms-1 bg-header-control border-bottom p-1 me-1 rounded">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <TextFilter
          field="sales_person"
          label="Sales Person"
          icon="bi-person"
          placeholder="Filter by salesperson..."
          value={filters.sales_person}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        <TextFilter
          field="sales_lead"
          label="Sales Lead"
          icon="bi-person-badge"
          placeholder="Filter by sales lead..."
          value={filters.sales_lead}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        <MultiSelectFilter
          field="owner"
          label="Owner"
          icon="bi-person-check"
          value={filters.owner}
          options={ownerOptions}
          onChange={handleFilterChange}
        />

        <TextFilter
          field="customer"
          label="Customer"
          icon="bi-building"
          placeholder="Filter by customer..."
          value={filters.customer}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        <TextFilter
          field="location"
          label="Location"
          icon="bi-pin-map"
          placeholder="Filter by location..."
          value={filters.location}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        <TextFilter
          field="state"
          label="State"
          icon="bi-geo-alt"
          placeholder="Filter by state..."
          value={filters.state}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        <TextFilter
          field="activity"
          label="Activity"
          icon="bi-list-task"
          placeholder="Filter by activity..."
          value={filters.activity}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        <MultiSelectFilter
          field="activity_type"
          label="Activity Type"
          icon="bi-tag"
          value={filters.activity_type}
          options={[
            { value: 'Minecraft', label: 'Minecraft' },
            { value: 'Roblox', label: 'Roblox' },
            { value: 'Python', label: 'Python' },
            { value: 'Scratch', label: 'Scratch' },
            { value: 'Web-Dev', label: 'Web-Dev' },
            { value: 'Code-In-Class', label: 'Code-In-Class' },
            { value: 'Sphero', label: 'Sphero' },
            { value: 'Canceled', label: 'Canceled' },
          ]}
          onChange={handleFilterChange}
        />

        <MultiSelectFilter
          field="device_types"
          label="Devices"
          icon="bi-laptop"
          value={filters.device_types}
          options={deviceTypeOptions}
          onChange={handleFilterChange}
        />

        <SelectFilter
          field="reviewed"
          label="Reviewed"
          icon="bi-clipboard-check"
          value={filters.reviewed}
          options={[
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
            { value: 'Unknown', label: 'Unknown' },
          ]}
          onChange={handleFilterChange}
        />

        <SelectFilter
          field="tech_setup_required"
          label="Tech Setup Required"
          icon="bi-gear"
          value={filters.tech_setup_required}
          options={[
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
          ]}
          onChange={handleFilterChange}
        />

        <SelectFilter
          field="tech_setup_completed"
          label="Tech Setup Completed"
          icon="bi-check-circle"
          value={filters.tech_setup_completed}
          options={[
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
          ]}
          onChange={handleFilterChange}
        />

        <SelectFilter
          field="invoiced"
          label="Invoiced"
          icon="bi-receipt"
          value={filters.invoiced}
          options={[
            { value: 'Sent', label: 'Sent' },
            { value: 'Received', label: 'Received' },
            { value: 'Need To Send', label: 'Need To Send' },
            { value: 'Unknown', label: 'Unknown' },
          ]}
          onChange={handleFilterChange}
        />

        <TextFilter
          field="invoice_number"
          label="Invoice"
          icon="bi-file-earmark-text"
          placeholder="Filter by invoice..."
          value={filters.invoice_number}
          onChange={handleFilterChange}
          isClearing={isClearing}
        />

        <SelectFilter
          field="customer_type"
          label="Customer Type"
          icon="bi-person-badge"
          value={filters.customer_type}
          options={[
            { value: 'New', label: 'New' },
            { value: 'Repeat', label: 'Repeat' },
          ]}
          onChange={handleFilterChange}
        />

        <SelectFilter
          field="sling_location"
          label="Sling Location"
          icon="bi-geo-alt"
          value={filters.sling_location}
          options={[
            { value: 'missing', label: 'Missing' },
            { value: 'set', label: 'Set' },
          ]}
          onChange={handleFilterChange}
        />

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <button
            className="btn btn-outline-danger btn-sm ms-auto"
            onClick={() => {
              setIsClearing(true);
              onClearFilters();
              setTimeout(() => setIsClearing(false), 0);
            }}
          >
            <i className="bi bi-x-circle me-1"></i>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
};

export default EventFilterBar;
